import { useEffect, useState } from "react";
import { Upload, Image as ImageIcon, Video, FileText, Palette, Save, Code2, Sparkles, Loader2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { uploadFile, fileUrl } from "@/lib/upload";
import { useAiCopy } from "@/components/media/useAiCopy";
import ScreensEditor from "@/components/ScreensEditor";
import { ScreensAllAiDialog } from "@/components/ScreensAllAiDialog";
import LivePreview from "@/components/LivePreview";
import SoundsEditor from "@/components/SoundsEditor";
import { RulesFields } from "@/components/RulesFields";
import { RulesVersions } from "@/components/RulesVersions";

function RulesCard({ contest, onSaved, draft, onGenerate, generating }) {
  const [v, setV] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV({ rules_text: contest.rules_text || "", rules_file_id: contest.rules_file_id || null, privacy_text: contest.privacy_text || "", privacy_file_id: contest.privacy_file_id || null, contact_email: contest.contact_email || "", contact_phone: contest.contact_phone || "" }); }, [contest]);
  useEffect(() => { if (draft != null) setV((x) => ({ ...x, rules_text: draft })); }, [draft]);
  const save = async () => {
    setBusy(true);
    try { await api.updateRules(contest.id, v); toast.success("Regolamento e contatti salvati"); onSaved?.(); } catch { toast.error("Salvataggio non riuscito"); } finally { setBusy(false); }
  };
  return (
    <Card className="p-5 bg-card border-border space-y-3" data-testid="rules-card">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="font-display font-bold text-lg flex-1">Regolamento e contatti</h3>
        <Button data-testid="rules-ai-generate" size="sm" variant="secondary" onClick={onGenerate} disabled={generating} className="gap-1.5">
          {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Genera con AI
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">Mostrati ai clienti (concorsi, totem, smartphone), ai punti vendita e nelle ricevute.</p>
      <RulesFields value={v} onChange={setV} />
      <RulesVersions contestId={contest.id} refreshKey={contest.rules_version} />
      <Button data-testid="save-rules-button" onClick={save} disabled={busy} className="w-full gap-2"><Save className="h-4 w-4" /> {busy ? "Salvataggio…" : "Salva regolamento e contatti"}</Button>
    </Card>
  );
}

const GAME_TYPES = [
  { k: "slot", l: "Slot Machine" },
  { k: "scratch", l: "Gratta e Vinci" },
  { k: "wheel", l: "Ruota della Fortuna" },
  { k: "boxes", l: "Scegli la Scatola" },
];
const TEXT_FIELDS = [
  ["welcome", "Titolo di benvenuto"], ["instructions", "Istruzioni"], ["win_title", "Titolo vincita"],
  ["win_message", "Messaggio vincita"], ["lose_title", "Titolo non vincita"], ["lose_message", "Messaggio non vincita"],
];

function UploadBox({ label, icon: Icon, accept, kind, fileId, onUploaded, preview }) {
  const [prog, setProg] = useState(null);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setProg(0);
    try {
      const doc = await uploadFile(f, kind, setProg);
      onUploaded(doc.id);
      toast.success(`${label} caricato`);
    } catch (err) {
      toast.error(err.message || "Caricamento non riuscito");
    } finally {
      setProg(null);
    }
  };
  return (
    <div className="rounded-lg bg-secondary/40 p-3 space-y-2" data-testid={`upload-${kind}`}>
      <Label className="flex items-center gap-1.5 text-xs"><Icon className="h-3.5 w-3.5" /> {label}</Label>
      {fileId && preview === "image" && <img src={fileUrl(fileId)} alt={label} className="h-20 rounded-md bg-white object-contain" />}
      {fileId && preview === "video" && <video src={fileUrl(fileId)} className="h-24 rounded-md" controls muted />}
      {fileId && preview === "pdf" && <a href={fileUrl(fileId)} target="_blank" rel="noreferrer" className="text-xs text-primary underline">Apri locandina</a>}
      <div className="flex items-center gap-2">
        <label className="flex-1 cursor-pointer">
          <input type="file" accept={accept} onChange={pick} className="hidden" data-testid={`upload-input-${kind}`} />
          <div className="h-9 rounded-md border border-dashed border-border grid place-items-center text-xs text-muted-foreground hover:border-primary">
            {prog !== null ? `Caricamento ${prog}%` : <span className="flex items-center gap-1"><Upload className="h-3.5 w-3.5" /> {fileId ? "Sostituisci" : "Carica file"}</span>}
          </div>
        </label>
        {fileId && <Button size="sm" variant="ghost" onClick={() => onUploaded(null)}>Rimuovi</Button>}
      </div>
    </div>
  );
}

function MediaCard({ m, setM, genPoster, posterBusy }) {
  const up = (k) => (id) => setM((x) => ({ ...x, [k]: id }));
  return (
    <Card className="p-5 bg-card border-border space-y-3">
      <h3 className="font-display font-bold text-lg flex items-center gap-2"><ImageIcon className="h-4 w-4" /> Media dell'iniziativa</h3>
      <UploadBox label="Logo iniziativa" icon={ImageIcon} accept="image/*" kind="logo" fileId={m.logo_file_id} preview="image" onUploaded={up("logo_file_id")} />
      <UploadBox label="Video di presentazione (mp4/webm)" icon={Video} accept="video/mp4,video/webm" kind="video" fileId={m.video_file_id} preview="video" onUploaded={up("video_file_id")} />
      <div className="space-y-1.5">
        <Label className="text-xs">oppure URL video (YouTube / Vimeo / mp4)</Label>
        <Input data-testid="video-url-input" value={m.video_url} onChange={(e) => setM((x) => ({ ...x, video_url: e.target.value }))} placeholder="https://www.youtube.com/watch?v=…" />
      </div>
      <UploadBox label="Folder / Locandina (PDF o immagine)" icon={FileText} accept="application/pdf,image/*" kind="poster" fileId={m.poster_file_id} preview={m.poster_file_id ? "image" : null} onUploaded={up("poster_file_id")} />
      <Button data-testid="gen-poster-button" variant="secondary" onClick={genPoster} disabled={posterBusy} className="w-full gap-2">
        {posterBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {posterBusy ? "Generazione in corso (30-60s)…" : "Genera locandina con AI (Gemini)"}
      </Button>
    </Card>
  );
}

function EmbedCard({ contestId }) {
  const embedUrl = `${window.location.origin}/embed/${contestId}`;
  const embedCode = `<iframe src="${embedUrl}" width="100%" height="820" style="border:0;border-radius:16px" allow="camera"></iframe>`;
  return (
    <Card className="p-5 bg-card border-border space-y-3">
      <h3 className="font-display font-bold text-lg flex items-center gap-2"><Code2 className="h-4 w-4" /> Inserisci in altre app</h3>
      <p className="text-xs text-muted-foreground">Incorpora il gioco in un sito o in un'altra app tramite iframe, oppure apri il link diretto sul totem.</p>
      <Input readOnly value={embedUrl} data-testid="embed-url" className="font-mono-code text-xs" />
      <Textarea readOnly value={embedCode} data-testid="embed-code" className="font-mono-code text-xs h-20" />
      <Button size="sm" variant="secondary" className="gap-2" onClick={() => { navigator.clipboard.writeText(embedCode); toast.success("Codice copiato"); }}>
        <Copy className="h-3.5 w-3.5" /> Copia codice embed
      </Button>
    </Card>
  );
}

const COLOR_FIELDS = [["primary_color", "Colore primario"], ["accent_color", "Colore accento"], ["bg_color", "Sfondo"]];

function GameStyleCard({ game, setGame, save, saving }) {
  const setK = (k) => (v) => setGame((g) => ({ ...g, [k]: v }));
  return (
    <Card className="p-5 bg-card border-border space-y-4">
      <h3 className="font-display font-bold text-lg flex items-center gap-2"><Palette className="h-4 w-4" /> Gioco e videate</h3>
      <div className="grid grid-cols-2 gap-2">
        {GAME_TYPES.map((g) => (
          <Button key={g.k} data-testid={`game-type-${g.k}`} size="sm" variant={game.type === g.k ? "default" : "secondary"} onClick={() => setK("type")(g.k)}>{g.l}</Button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {COLOR_FIELDS.map(([k, l]) => (
          <div key={k} className="space-y-1">
            <Label className="text-xs">{l}</Label>
            <input data-testid={`color-${k}`} type="color" value={game[k] || "#000000"} onChange={(e) => setK(k)(e.target.value)} className="h-9 w-full rounded-md border border-border bg-transparent" />
          </div>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {TEXT_FIELDS.map(([k, l]) => (
          <div key={k} className="space-y-1">
            <Label className="text-xs">{l}</Label>
            <Input data-testid={`text-${k}`} value={game[k] || ""} onChange={(e) => setK(k)(e.target.value)} />
          </div>
        ))}
      </div>
      <Button data-testid="save-media-game-button" onClick={save} disabled={saving} className="w-full gap-2">
        <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva gioco e grafica"}
      </Button>
    </Card>
  );
}

const APPLY_LABEL = { screens: "Applica ai testi videate", rules: "Inserisci nel regolamento" };

function AiCopyPanel({ ai, setAi, runAi, applyAi }) {
  const upd = (k) => (e) => setAi((a) => ({ ...a, [k]: e.target.value }));
  return (
    <Card className="p-5 bg-card border-border space-y-3" data-testid="ai-copy-panel">
      <h3 className="font-display font-bold text-lg flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-400" /> Assistente AI testi</h3>
      <div className="grid grid-cols-2 gap-2">
        <select data-testid="ai-task-select" value={ai.task} onChange={upd("task")} className="h-9 rounded-md border border-border bg-background px-2 text-sm">
          <option value="description">Descrizione concorso</option>
          <option value="rules">Regolamento</option>
          <option value="screens">Testi videate (applica)</option>
          <option value="email">Email ricevuta</option>
          <option value="social">Post social</option>
        </select>
        <select data-testid="ai-model-select" value={ai.model} onChange={upd("model")} className="h-9 rounded-md border border-border bg-background px-2 text-sm">
          <option value="gpt">ChatGPT (GPT-5.4)</option>
          <option value="claude">Claude Sonnet 4.6</option>
          <option value="gemini">Gemini 3 Flash</option>
        </select>
      </div>
      <Input data-testid="ai-extra-input" value={ai.extra} onChange={upd("extra")} placeholder="Indicazioni aggiuntive (tono, target, dettagli…)" />
      <Button data-testid="ai-generate-button" onClick={runAi} disabled={ai.busy} className="w-full gap-2">
        {ai.busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Genera
      </Button>
      {ai.out && (
        <>
          <Textarea data-testid="ai-output" readOnly value={ai.out} className="h-40 text-sm" />
          <Button data-testid="ai-apply-button" size="sm" variant="secondary" onClick={applyAi} className="gap-2">
            <Copy className="h-3.5 w-3.5" /> {APPLY_LABEL[ai.task] || "Copia testo"}
          </Button>
        </>
      )}
    </Card>
  );
}

export default function MediaGameTab({ contest, onSaved }) {
  const [m, setM] = useState({ logo_file_id: null, video_url: "", video_file_id: null, poster_file_id: null });
  const [game, setGame] = useState({});
  const [saving, setSaving] = useState(false);
  const [posterBusy, setPosterBusy] = useState(false);
  const [rulesDraft, setRulesDraft] = useState(null);
  const { ai, setAi, runAi, applyAi, generateRules } = useAiCopy(contest, setGame, setRulesDraft);

  useEffect(() => {
    setM({ logo_file_id: contest.logo_file_id || null, video_url: contest.video_url || "", video_file_id: contest.video_file_id || null, poster_file_id: contest.poster_file_id || null });
    setGame({ type: "slot", primary_color: "#2f6fe4", accent_color: "#f5b82e", bg_color: "#ffffff", ...(contest.game || {}) });
  }, [contest]);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateMedia(contest.id, { ...m, video_url: m.video_url || null, game });
      toast.success("Gioco e grafica salvati");
      onSaved?.();
    } catch {
      toast.error("Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  const genPoster = async () => {
    setPosterBusy(true);
    try {
      const doc = await api.aiPoster(contest.id, { prompt: ai.extra, style: "moderno e colorato" });
      setM((x) => ({ ...x, poster_file_id: doc.id }));
      toast.success("Locandina generata: salva per confermare");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generazione non riuscita");
    } finally {
      setPosterBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="media-game-tab">
      <div className="space-y-5">
        <Card className="p-4 border-violet-500/30 bg-violet-500/5" data-testid="media-ai-backgrounds-card">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[220px]">
              <h3 className="font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-500" /> Sfondi delle videate con l'IA</h3>
              <p className="text-xs text-muted-foreground mt-1">Genera in un clic le 4 videate (attesa, estrazione, vincita, non vincita) dal tema e dai premi del concorso, già ritagliate per TV, totem e app. Poi "Salva videate".</p>
            </div>
            <ScreensAllAiDialog prominent contestId={contest.id} customFormats={game.custom_formats || []} onApply={(all) => setGame({ ...game, screens: all })} />
          </div>
        </Card>
        <MediaCard m={m} setM={setM} genPoster={genPoster} posterBusy={posterBusy} />
        <EmbedCard contestId={contest.id} />
      </div>
      <div className="space-y-5">
        <LivePreview contestId={contest.id} game={game} />
        <GameStyleCard game={game} setGame={setGame} save={save} saving={saving} />
        <SoundsEditor game={game} setGame={setGame} />
        <RulesCard contest={contest} onSaved={onSaved} draft={rulesDraft} onGenerate={generateRules} generating={ai.busy && ai.task === "rules"} />
        <ScreensEditor contestId={contest.id} game={game} setGame={setGame} />
        <Button data-testid="save-media-game-button-2" onClick={save} disabled={saving} className="w-full gap-2">
          <Save className="h-4 w-4" /> {saving ? "Salvataggio…" : "Salva videate"}
        </Button>
        <AiCopyPanel ai={ai} setAi={setAi} runAi={runAi} applyAi={applyAi} />
      </div>
    </div>
  );
}
